import { Map, List } from 'immutable'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import React, { useMemo, useCallback } from 'react'
import { truncate } from '../../../theme/web-app/index'
import NotAvailable, { TYPE_NOT_AVAILABLE_OVERLAY } from '../../NotAvailable/index'
import WatchAccess, { ACCESS_CHECK_SHARE_FEATURE_GEO } from '../../WatchAccess/index'
import WatchAccessAllowed from '../../WatchAccessAllowed/index'
import WatchAccessDenied from '../../WatchAccessDenied/index'
import { ICON_TYPES } from '../../Icon.v2/index'
import { H5 } from '../../Heading/index'
import Row from '../../Row.v2/index'
import Button, { TYPES } from '../../Button/index'
import { getShareV3Link } from '../../../services/share/index'
import { isEntitled } from '../../../services/subscription/index'

function createAccessor (Accessor, item) {
  if (!item) return null
  const url = item.get('url', '') || ''
  const title = item.get('title', '')
  return <Accessor title={title} to={url} />
}

function renderItem (item, params, props) {
  const {
    auth,
    staticText,
    language,
    portal,
    user,
  } = props
  const { focused } = params

  if (!item) {
    return <div className="portal-playlist-v2__item" />
  }

  const preview = item.get('preview', Map())
  const feature = item.get('feature', Map())
  const title = item.get('title')
  const description = item.get('body')
  const image = item.get('imageWithText')
  const isSharedAllowed = item.getIn(['feature', 'shareAllowed'], false)
  const isUserEntitled = isEntitled(auth.get('subscriptions', List()))
  const descriptionSplit = truncate(description, 100)
  const isShareItem = isSharedAllowed && !isUserEntitled

  const url = isShareItem
    ? getShareV3Link({
      userLang: language,
      userRfd: portal.getIn(['data', 'userReferralId']),
      userAffId: portal.getIn(['data', 'everflowAffiliateId']),
      userUid: portal.getIn(['data', 'uuid']),
      contentUrl: item.get('url'),
      contentId: item.get('id'),
      contentType: item.get('contentType'),
    })
    : item.get('url')

  const imageTag = (
    <img
      className="portal-playlist-v2__image"
      alt={title}
      src={image}
    />
  )

  return (
    <div className="portal-playlist-v2__item">
      <WatchAccess
        accessCheck={ACCESS_CHECK_SHARE_FEATURE_GEO}
        feature={feature}
        preview={preview}
        auth={auth}
        user={user}
      >
        <WatchAccessDenied>
          <NotAvailable
            message={staticText.get('notAvailableInRegion')}
            type={TYPE_NOT_AVAILABLE_OVERLAY}
          />
          {imageTag}
        </WatchAccessDenied>
        <WatchAccessAllowed>
          <Button
            className="portal-playlist-v2__item-btn"
            url={url}
            scrollToTop={true}
            directLink={isShareItem}
          >
            {imageTag}
          </Button>
          {focused
            ? <div className="portal-playlist-v2__meta">
              <Button
                className="portal-playlist-v2__meta-link"
                type={TYPES.LINK}
                url={url}
                scrollToTop={true}
                directLink={isShareItem}
              />
              <H5>{title}</H5>
              <p className="portal-playlist-v2__body">
                {descriptionSplit}
              </p>
            </div>
            : null
          }
        </WatchAccessAllowed>
      </WatchAccess>
    </div>
  )
}

export default function Playlist (props) {
  const {
    ctaOnClick,
    ctaLabel,
    editable,
    setMode,
    portal,
    modes,
    light,
    label,
    items,
    auth,
  } = props
  const isPortalOwner = portal.get('isPortalOwner')

  const vars = useMemo(() => ({
    className: `portal-playlist-v2 ${light ? 'portal-playlist-v2--light' : ''}`,
  }), [light])

  const editPlaylist = useCallback(() => {
    setMode(modes.PLAYLIST_EDIT)
  }, [])

  return (
    <section className={vars.className}>
      <Row
        className="portal-playlist-v2__row"
        createAccessor={createAccessor}
        ctaOnClick={ctaOnClick}
        ctaLabel={ctaLabel}
        items={items}
        label={label}
        auth={auth}
        inverted
      >{(item, data) => renderItem(item, data, props)}
      </Row>
      {isPortalOwner && editable ? (
        <div className="portal-playlist-v2__actions">
          <Button
            className="portal-playlist-v2__action-edit"
            type={TYPES.ICON_PRIMARY}
            icon={ICON_TYPES.EDIT}
            onClick={editPlaylist}
            shadow
          />
        </div>
      ) : null}
    </section>
  )
}

Playlist.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  items: ImmutablePropTypes.list.isRequired,
  portal: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  label: PropTypes.string.isRequired,
  kind: PropTypes.string.isRequired,
  ctaOnClick: PropTypes.func,
  ctaLabel: PropTypes.string,
  editable: PropTypes.bool,
  light: PropTypes.bool,
  language: PropTypes.string.isRequired,
}
